import { Copy, FileText } from "lucide-react";
import SuccessCheckmark from "./SuccessCheckmark";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import useStructuredData from "../hooks/useStructuredData";
import { COMPANY_REGISTRATION } from "../data/legalRegistrations";

export default function CompanyRegistrationBlock() {
  const { copy, copied } = useCopyToClipboard();
  const record = COMPANY_REGISTRATION;

  useStructuredData(
    record
      ? {
          "@context": "https://schema.org",
          "@type": "PostalAddress",
          name: record.legalName,
          streetAddress: record.registeredAddress,
        }
      : null
  );

  if (!record) {
    return (
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
        <h3 className="font-display font-semibold text-ocean-950">Corporate Registration</h3>
        <p className="mt-2 text-sm text-ocean-800/60">
          CIN and registered office details will be published here once incorporation is complete.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
      <h3 className="font-display font-semibold text-ocean-950">Corporate Registration</h3>
      <dl className="mt-3 space-y-3 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-ocean-800/50">CIN</dt>
          <dd className="mt-0.5 font-mono text-ocean-950">{record.cin}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-ocean-800/50">Legal Name</dt>
          <dd className="mt-0.5 text-ocean-950">{record.legalName}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-ocean-800/50">Incorporation Date</dt>
          <dd className="mt-0.5 text-ocean-950">{record.incorporationDate}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-ocean-800/50">Registered Office</dt>
          <dd className="mt-0.5 flex items-start gap-2 text-ocean-950">
            {record.registeredAddress}
            <button
              type="button"
              onClick={() => copy(record.registeredAddress)}
              aria-label="Copy address"
              className="mt-0.5 shrink-0 text-ocean-800/40 hover:text-teal-600"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </dd>
        </div>
      </dl>

      {record.certificateUrl && (
        <a
          href={record.certificateUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-700"
        >
          <FileText className="h-4 w-4" />
          View Certificate
        </a>
      )}

      <div
        role="status"
        aria-live="polite"
        className={`mt-3 flex items-center gap-1 text-xs font-medium text-teal-600 transition-opacity ${copied ? "opacity-100" : "opacity-0"}`}
      >
        {copied && (
          <>
            <SuccessCheckmark className="h-3 w-3" /> Address copied to clipboard
          </>
        )}
      </div>
    </div>
  );
}
